/**
 * KioskPage — dedicated walk-up kiosk (/kiosk/{org}).
 * Book + Sign waiver tabs (branding-free embeds — the kiosk header carries the
 * logo + company name), plus an optional camera check-in view when the kiosk
 * was opened with a scanner session (?scanner=token, generated from the
 * Walk-Ins tab when the operator has Check-In enabled).
 * A combined masked "recent activity" bar (signatures + check-ins) sits under
 * the waiver view so front-desk staff can glance without exposing details.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CalendarDays, PenLine, Camera, CheckCircle2, Zap, PartyPopper, CreditCard, X, Menu, Search, ReceiptText } from 'lucide-react';
import api from '../utils/api';
import KioskCheckIn from '../components/KioskCheckIn';
import KioskBookingSheet from '../components/KioskBookingSheet';

export default function KioskPage() {
  const { orgSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // 🚶 Kiosk Direct — walk-in fast path (?mode=direct): contact-first widget,
  // "available right now" slots, and a post-payment waiver hand-off (?code=BK-XXX).
  const directMode = searchParams.get('mode') === 'direct';
  const paidCode = (searchParams.get('code') || '').trim().toUpperCase() || null;
  const wasCancelled = searchParams.get('cancelled') === '1';
  // Waiver-first flow: signatures were collected BEFORE payment — skip the handoff
  const alreadyWaived = searchParams.get('waived') === '1';
  const [showCancelled, setShowCancelled] = useState(wasCancelled);
  const [org, setOrg] = useState(null);
  const [posCfg, setPosCfg] = useState(null);
  const [tab, setTab] = useState(paidCode && !alreadyWaived ? 'waiver' : 'book');
  const [signers, setSigners] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [sheetCode, setSheetCode] = useState(null);
  const [scannerToken, setScannerToken] = useState(null);
  // 🍔 POS shell — the header hamburger owns navigation (Book / Sign waiver /
  // Check-in / Today's bookings). The old pill tabs + bottom Recent strip are gone.
  const [menuOpen, setMenuOpen] = useState(false);
  const [todaySearch, setTodaySearch] = useState('');
  // 🚶 Kiosk Direct scrolling: the widget's INNER document owns scrolling
  // (direct-scroll mode — body is the scroll container, same as the waiver
  // iframe). Auto-height + parent-scroll was dropped: cross-frame scroll
  // chaining is unreliable (dead trackpad/touch scrolling on real devices).
  // The widget mirrors its slide-up checkout bar to us over BF_DIRECT_BAR.
  const bookIframeRef = useRef(null);
  const [directBar, setDirectBar] = useState(null);
  // 🖊️ POS waiver overlay — shell-owned so it centers over the WHOLE screen
  // (header included) with a blurred backdrop. { verify, signed } | null
  const [posWaiver, setPosWaiver] = useState(null);
  // 🖥️ POS boot splash — full-screen brand takeover (same feel as the Helm /
  // Connect entry animations) until the widget reports its tiles are painted.
  const [posBoot, setPosBoot] = useState(directMode ? 'hold' : 'gone'); // hold → exit → gone

  useEffect(() => {
    if (!directMode || posBoot === 'gone') return undefined;
    // Failsafe: never trap the kiosk behind the splash
    const t = setTimeout(() => setPosBoot((p) => (p === 'hold' ? 'exit' : p)), 15000);
    return () => clearTimeout(t);
  }, [directMode, posBoot]);
  useEffect(() => {
    if (posBoot !== 'exit') return undefined;
    const t = setTimeout(() => setPosBoot('gone'), 650);
    return () => clearTimeout(t);
  }, [posBoot]);

  useEffect(() => {
    if (!directMode) return;
    const onMsg = (e) => {
      const d = e?.data;
      if (!d || typeof d !== 'object') return;
      // Waiver overlay iframe → relay signature events down to the widget
      if (d.type === 'BF_WAIVER_SIGNED' || d.type === 'BF_POS_WAIVER_DONE') {
        if (e.source !== bookIframeRef.current?.contentWindow) {
          try { bookIframeRef.current?.contentWindow?.postMessage(d, '*'); } catch (err) { /* noop */ }
          if (d.type === 'BF_WAIVER_SIGNED') setPosWaiver((p) => (p ? { ...p, signed: (p.signed || 0) + 1 } : p));
          if (d.type === 'BF_POS_WAIVER_DONE') setPosWaiver(null);
        }
        return;
      }
      if (e.source !== bookIframeRef.current?.contentWindow) return;
      if (d.type === 'BF_POS_OPEN_WAIVER') {
        setPosWaiver({ verify: d.verify || '1', signed: d.signed || 0 });
        return;
      }
      if (d.type === 'BF_POS_READY') {
        setPosBoot((p) => (p === 'hold' ? 'exit' : p));
        return;
      }
      if (d.type === 'BF_DIRECT_BAR') {
        if (d.visible) {
          setDirectBar({
            label: d.label || 'Complete booking',
            busy: !!d.busy,
            brand: d.brand || '#2563EB',
            brandText: d.brandText || '#ffffff',
          });
          try { e.source.postMessage({ type: 'BF_DIRECT_BAR_ACK' }, '*'); } catch (err) { /* noop */ }
        } else {
          setDirectBar(null);
        }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [directMode]);

  const postToBook = (type) => {
    try { bookIframeRef.current?.contentWindow?.postMessage({ type }, '*'); } catch (e) { /* noop */ }
  };

  // Close the waiver overlay + tell the widget to refresh its sheet (unlock button)
  const closePosWaiver = () => {
    setPosWaiver(null);
    postToBook('BF_POS_WAIVER_DONE');
  };

  useEffect(() => {
    api.get(`/flows/public/${orgSlug}/walk-ins`)
      .then((r) => { setOrg(r.data?.organization || null); setPosCfg(r.data?.pos_config || null); })
      .catch(() => setOrg({ name: orgSlug }));
  }, [orgSlug]);

  // Validate the scanner session handed over from the Walk-Ins tab
  useEffect(() => {
    const t = searchParams.get('scanner');
    if (!t) return;
    api.post('/v2/public/check-in/validate-scanner-token', { token: t })
      .then((r) => { if (r.data?.valid) setScannerToken(t); })
      .catch(() => { /* expired/invalid — kiosk simply runs without check-in */ });
  }, []);

  const loadSigners = useCallback(() => {
    api.get(`/v2/public/org/${orgSlug}/recent-waiver-signers`)
      .then((r) => setSigners(r.data?.signers || []))
      .catch(() => { /* noop */ });
  }, [orgSlug]);

  const loadCheckIns = useCallback(() => {
    if (!scannerToken) return;
    api.post('/v2/public/check-in/recent-kiosk', { scannerToken })
      .then((r) => setCheckIns(r.data?.checkIns || []))
      .catch(() => { /* noop */ });
  }, [scannerToken]);

  const loadBookings = useCallback(() => {
    api.get(`/v2/public/org/${orgSlug}/recent-kiosk-bookings`)
      .then((r) => setBookings(r.data?.bookings || []))
      .catch(() => { /* noop */ });
  }, [orgSlug]);

  useEffect(() => {
    loadSigners();
    loadCheckIns();
    loadBookings();
    const t = setInterval(() => { loadSigners(); loadCheckIns(); loadBookings(); }, 30000);
    return () => clearInterval(t);
  }, [loadSigners, loadCheckIns, loadBookings]);

  // 🚶 Kiosk Direct payment return — settle the payment IMMEDIATELY (the same
  // Stripe-verified confirm-payment call booking-success.html makes for the
  // standard flow) so emails fire and the activity strip flips to Paid without
  // waiting on the webhook.
  // Phases: 'loading' (opaque white from the very first paint — the kiosk UI
  // never flashes behind) → 'anim' (check draw) → 'fade' (opacity out) → 'done'.
  const [confirmPhase, setConfirmPhase] = useState(paidCode ? 'loading' : 'done');
  useEffect(() => {
    if (!paidCode) return;
    let alive = true;
    const minHold = new Promise((r) => setTimeout(r, 900));
    const settle = api.post(`/v2/public/bookings/${encodeURIComponent(paidCode)}/confirm-payment`)
      .catch(() => { /* webhook remains the fallback path */ })
      .finally(() => { loadBookings(); loadSigners(); });
    Promise.all([minHold, settle]).then(() => { if (alive) setConfirmPhase('anim'); });
    return () => { alive = false; };
  }, [paidCode, loadBookings, loadSigners]);
  useEffect(() => {
    if (confirmPhase === 'anim') {
      const t = setTimeout(() => setConfirmPhase('fade'), 2400);
      return () => clearTimeout(t);
    }
    if (confirmPhase === 'fade') {
      const t = setTimeout(() => setConfirmPhase('done'), 550);
      return () => clearTimeout(t);
    }
  }, [confirmPhase]);

  useEffect(() => {
    if (tab !== 'book') { loadSigners(); loadCheckIns(); loadBookings(); }
  }, [tab, loadSigners, loadCheckIns, loadBookings]);

  const brand = org?.primary_color || '#2563EB';
  // 🇦🇪 UAE orgs run the kiosk bilingually — English left, Arabic right
  const isUAE = org?.currency === 'AED';

  // Combined masked activity — bookings + waiver signatures + check-ins, freshest first
  const activity = useMemo(() => [
    ...bookings.map((b) => ({
      type: 'booking', label: b.name, code: b.code, mins: b.minutesAgo,
      paid: ['paid', 'completed'].includes(String(b.paymentStatus || '')),
      service: b.serviceName, time: b.time,
    })),
    ...signers.map((s) => ({ type: 'signed', label: s.name, code: s.booking_code, mins: s.minutes_ago })),
    ...checkIns.map((c) => ({ type: 'checkin', label: c.maskedName, code: c.confirmationCode, mins: c.minutesAgo })),
  ].sort((a, b) => (a.mins ?? 0) - (b.mins ?? 0)).slice(0, 10), [bookings, signers, checkIns]);

  // (pill-tab helper removed — the hamburger menu owns navigation now)

  return (
    <div className="h-screen flex flex-col bg-gray-50 overscroll-none" style={{ height: '100dvh' }} data-testid="kiosk-page">
      {/* Header — airy, no hard divider */}
      <header className="bg-white relative z-10 px-4 sm:px-6 py-3 flex items-center gap-3 flex-shrink-0">
        {org?.logo_url ? (
          <img src={org.logo_url} alt={org?.name || ''} className="h-9 w-9 rounded-lg object-contain flex-shrink-0" />
        ) : (
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0"
            style={{ backgroundColor: brand }}
          >
            {(org?.name || orgSlug || 'B').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1 hidden sm:block">
          <p className="text-sm font-semibold text-gray-900 truncate" data-testid="kiosk-org-name">{org?.name || ''}</p>
          <p className="text-[11px] text-gray-400 flex items-center gap-1">
            {directMode && <Zap className="w-3 h-3 text-amber-500" />}
            {directMode ? 'Kiosk Direct' : 'Walk-up kiosk'}
          </p>
        </div>

        {/* 🍔 Hamburger — single navigation point, top right (POS shell) */}
        <div className="flex-1 sm:flex-none flex justify-end items-center min-w-0 relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="kiosk-menu-btn"
            aria-label="Menu"
            className="h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-[65]" onClick={() => setMenuOpen(false)} data-testid="kiosk-menu-overlay" />
              <div
                className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-[0_20px_50px_-12px_rgba(16,24,40,0.25)] ring-1 ring-gray-100 p-1.5 z-[66]"
                data-testid="kiosk-menu"
              >
                {[
                  { key: 'book', Icon: CalendarDays, label: 'Services', ar: 'الخدمات', testid: 'kiosk-tab-book' },
                  { key: 'waiver', Icon: PenLine, label: 'Sign waiver', ar: 'وقّع التنازل', testid: 'kiosk-tab-waiver' },
                  ...(scannerToken ? [{ key: 'checkin', Icon: Camera, label: 'Check-in', ar: 'تسجيل الوصول', testid: 'kiosk-tab-checkin' }] : []),
                  { key: 'today', Icon: ReceiptText, label: "Today's bookings", ar: 'حجوزات اليوم', testid: 'kiosk-tab-today' },
                ].map(({ key, Icon, label, ar, testid }) => (
                  <button
                    key={key}
                    onClick={() => { setTab(key); setMenuOpen(false); }}
                    data-testid={testid}
                    className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-semibold text-left transition-colors ${
                      tab === key ? 'text-white' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    style={tab === key ? { backgroundColor: brand } : undefined}
                  >
                    <Icon className="w-4.5 h-4.5 w-[18px] h-[18px] flex-shrink-0" />
                    <span className="flex-1">{label}</span>
                    {isUAE && <span dir="rtl" lang="ar" className="opacity-80 text-xs">{ar}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </header>

      {/* 🚶 Kiosk Direct: payment confirmed — hand straight into the waiver */}
      {paidCode && (
        <div
          className="bg-emerald-50 px-4 sm:px-6 py-3 flex items-center gap-3 flex-shrink-0"
          data-testid="kiosk-paid-banner"
        >
          <PartyPopper className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-emerald-800">
              Payment confirmed — booking <span className="font-mono">{paidCode}</span>
            </p>
            <p className="text-xs text-emerald-600">{alreadyWaived ? "You're all set — waiver signed and ticket on its way. See you on board!" : 'One last step: sign your waiver below. Your ticket is on its way to your email.'}</p>
          </div>
          {isUAE && (
            <div dir="rtl" lang="ar" className="hidden sm:block text-right flex-shrink-0" data-testid="kiosk-paid-banner-ar">
              <p className="text-sm font-bold text-emerald-800">تم تأكيد الدفع</p>
              <p className="text-xs text-emerald-600">خطوة أخيرة: وقّع التنازل أدناه.</p>
            </div>
          )}
          <button
            onClick={() => navigate(`/kiosk/${orgSlug}${directMode ? '?mode=direct' : ''}`, { replace: true })}
            data-testid="kiosk-new-guest-btn"
            aria-label="Done — next guest"
            title="Done — next guest"
            className="flex-shrink-0 h-8 w-8 rounded-full bg-white text-emerald-700 shadow-sm hover:bg-emerald-100 transition-colors flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 🚶 Kiosk Direct: guest backed out of Stripe — gentle notice, spot auto-releases */}
      {showCancelled && !paidCode && (
        <div
          className="bg-amber-50 px-4 sm:px-6 py-3 flex items-center gap-3 flex-shrink-0"
          data-testid="kiosk-cancelled-banner"
        >
          <CreditCard className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-amber-800">Payment wasn't completed — no charge was made</p>
            <p className="text-xs text-amber-600">The spot was released. Pick a departure below to try again.</p>
          </div>
          {isUAE && (
            <div dir="rtl" lang="ar" className="hidden sm:block text-right flex-shrink-0" data-testid="kiosk-cancelled-banner-ar">
              <p className="text-sm font-bold text-amber-800">لم يكتمل الدفع — لم يتم خصم أي مبلغ</p>
              <p className="text-xs text-amber-600">تم تحرير المقعد. اختر موعدًا أدناه للمحاولة مرة أخرى.</p>
            </div>
          )}
          <button
            onClick={() => { setShowCancelled(false); navigate(`/kiosk/${orgSlug}${directMode ? '?mode=direct' : ''}`, { replace: true }); }}
            data-testid="kiosk-cancelled-dismiss"
            aria-label="Dismiss"
            className="flex-shrink-0 h-8 w-8 rounded-full bg-white text-amber-700 shadow-sm hover:bg-amber-100 transition-colors flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Content — booking + waiver iframes stay mounted so switching never loses
          progress; opacity crossfade keeps tab changes buttery instead of a hard cut.
          Each iframe sits in its own touch-scrollable layer: iPads/tablets often
          refuse to scroll a height-pinned iframe's inner document otherwise. */}
      <div className="flex-1 relative overflow-hidden">
        <div
          className={`absolute inset-0 overflow-y-auto overscroll-contain ${tab === 'book' ? '' : 'hidden'}`}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <iframe
            ref={bookIframeRef}
            src={`/services-grid.html?org=${orgSlug}&flow=${directMode ? 'kiosk-direct' : 'walk-ins'}&embed=true&hideLogo=true&hideName=true${directMode ? `&mode=direct&pos=1&successReturnPath=${encodeURIComponent(`/kiosk/${orgSlug}?mode=direct`)}` : ''}`}
            title="Book an experience"
            scrolling="yes"
            className="w-full h-full border-0 bg-white"
            allow="payment"
            data-testid="kiosk-book-iframe"
          />
        </div>
        <div
          className={`absolute inset-0 overflow-y-auto overscroll-contain ${tab === 'waiver' ? '' : 'hidden'}`}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <iframe
            src={`/sign/${orgSlug}?kiosk=true${paidCode ? `&booking=${encodeURIComponent(paidCode)}` : ''}${directMode ? `&pos=1&verify=${posCfg && posCfg.waiverOtp === false ? '0' : '1'}` : ''}`}
            title="Sign the waiver"
            scrolling="yes"
            className="w-full h-full border-0 bg-white"
            data-testid="kiosk-waiver-iframe"
          />
        </div>
        {tab === 'checkin' && scannerToken && (
          <div className="absolute inset-0 bg-white overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <KioskCheckIn scannerToken={scannerToken} brand={brand} checkIns={checkIns} onRefresh={loadCheckIns} />
          </div>
        )}
        {tab === 'today' && (
          <div className="absolute inset-0 bg-white overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }} data-testid="kiosk-today-view">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
              <div className="relative mb-4">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={todaySearch}
                  onChange={(e) => setTodaySearch(e.target.value)}
                  placeholder={isUAE ? 'Search a name or code · ابحث بالاسم أو الرمز' : 'Search a name or booking code…'}
                  className="w-full h-12 pl-10 pr-4 rounded-2xl bg-white ring-1 ring-gray-200 text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': brand }}
                  data-testid="kiosk-today-search"
                />
              </div>
              {(() => {
                const q = todaySearch.trim().toLowerCase();
                const rows = q
                  ? activity.filter((a) => `${a.label || ''} ${a.code || ''} ${a.service || ''}`.toLowerCase().includes(q))
                  : activity;
                if (rows.length === 0) {
                  return (
                    <div className="text-center py-16" data-testid="kiosk-today-empty">
                      <ReceiptText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm font-semibold text-gray-500">{q ? 'No matches' : 'Nothing yet today'}</p>
                      <p className="text-xs text-gray-400 mt-1">{q ? 'Try a different name or code' : 'Bookings, signatures and check-ins will appear here'}</p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-2" data-testid="kiosk-today-list">
                    {rows.map((a, i) => {
                      const tone = a.type === 'signed'
                        ? 'bg-emerald-50 text-emerald-700'
                        : a.type === 'checkin'
                          ? 'bg-blue-50 text-blue-700'
                          : a.paid ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-700';
                      const Icon = a.type === 'signed' ? PenLine : a.type === 'checkin' ? CheckCircle2 : CreditCard;
                      const kind = a.type === 'signed' ? 'Waiver signed' : a.type === 'checkin' ? 'Checked in' : a.paid ? 'Booking · paid' : 'Booking · unpaid';
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => a.code && setSheetCode(a.code)}
                          disabled={!a.code}
                          className={`w-full bg-gray-50/60 rounded-2xl ring-1 ring-gray-100 px-4 py-3.5 flex items-center gap-3 text-left transition-transform ${a.code ? 'active:scale-[0.99] cursor-pointer hover:ring-gray-200' : 'cursor-default'}`}
                          data-testid={`kiosk-today-row-${i}`}
                        >
                          <span className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${tone}`}>
                            <Icon className="w-4 h-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-gray-900 truncate">{a.label}</span>
                            <span className="block text-xs text-gray-400 truncate">
                              {kind}{a.service ? ` · ${a.service}` : ''}{a.time ? ` · ${a.time}` : ''}
                            </span>
                          </span>
                          {a.code && <span className="font-mono text-[11px] text-gray-400 flex-shrink-0">{a.code}</span>}
                          <span className="text-[11px] text-gray-300 flex-shrink-0">{(a.mins ?? 0) < 1 ? 'now' : `${a.mins}m`}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* (Bottom "Recent" strip removed — Today's bookings lives in the hamburger
          menu now, full-screen with search, per the POS shell redesign.) */}

      {/* 🎉 Payment return — opaque white from the very first paint (zero flash of
          the kiosk UI): loading spinner while the payment settles, then the check
          draw, then a soft fade into the waiver */}
      {paidCode && confirmPhase !== 'done' && (
        <div
          className={`fixed inset-0 z-[70] bg-white flex flex-col items-center justify-center gap-4 transition-opacity duration-500 ${confirmPhase === 'fade' ? 'opacity-0' : 'opacity-100'}`}
          data-testid="kiosk-success-overlay"
        >
          {confirmPhase === 'loading' ? (
            <div className="flex flex-col items-center gap-5" data-testid="kiosk-confirm-loading">
              <svg className="animate-spin" width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke={brand} strokeWidth="3" strokeLinecap="round" />
              </svg>
              <div className="text-center">
                <p className="text-base font-semibold text-gray-700">Confirming your payment…</p>
                {isUAE && <p dir="rtl" lang="ar" className="text-sm text-gray-500 mt-1">جارٍ تأكيد الدفع…</p>}
              </div>
            </div>
          ) : (
            <>
              <svg width="96" height="96" viewBox="0 0 96 96" fill="none" aria-hidden="true">
                <circle
                  cx="48" cy="48" r="42" stroke={brand} strokeWidth="5" strokeLinecap="round"
                  strokeDasharray="264" strokeDashoffset="264"
                  style={{ animation: 'kioskDraw 0.7s ease-out 0.1s forwards' }}
                />
                <path
                  d="M32 49.5 L43.5 61 L65 37" stroke={brand} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
                  strokeDasharray="50" strokeDashoffset="50"
                  style={{ animation: 'kioskDraw 0.45s ease-out 0.7s forwards' }}
                />
              </svg>
              <div className="text-center" style={{ animation: 'kioskRise 0.5s ease-out 0.55s both' }}>
                <p className="text-2xl font-extrabold text-gray-900">
                  Payment confirmed
                  {isUAE && <span dir="rtl" lang="ar" className="block text-lg font-bold text-gray-700 mt-0.5">تم تأكيد الدفع</span>}
                </p>
                <p className="text-sm font-mono text-gray-500 mt-1" data-testid="kiosk-success-code">{paidCode}</p>
                <p className="text-sm text-gray-400 mt-3">
                  {alreadyWaived ? "You're all set — see you on board!" : 'One last step — signing your waiver…'}
                  {isUAE && <span dir="rtl" lang="ar" className="block mt-0.5">{alreadyWaived ? 'كل شيء جاهز — نراكم على متن القارب!' : 'خطوة أخيرة — توقيع التنازل…'}</span>}
                </p>
              </div>
            </>
          )}
          <style>{`
            @keyframes kioskDraw { to { stroke-dashoffset: 0; } }
            @keyframes kioskRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          `}</style>
        </div>
      )}

      {/* 🚶 Kiosk Direct — native checkout bar pinned to the real screen bottom.
          Mirrors the widget's bar state (label/total/busy) via BF_DIRECT_BAR. */}
      {directMode && directBar && tab === 'book' && (
        <div
          className="fixed left-0 right-0 bottom-0 z-[60] flex items-stretch gap-2.5 px-3.5 pt-2.5 bg-white/95 backdrop-blur-md border-t border-gray-200"
          style={{ paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))' }}
          data-testid="kiosk-direct-bar"
        >
          <button
            type="button"
            disabled={directBar.busy}
            onClick={() => postToBook('BF_DIRECT_BAR_CLICK')}
            className="flex-1 rounded-full text-[15px] font-bold py-3.5 px-5 whitespace-nowrap overflow-hidden text-ellipsis transition-transform active:scale-[0.98] disabled:opacity-60"
            style={{ backgroundColor: directBar.brand, color: directBar.brandText }}
            data-testid="kiosk-direct-bar-complete-btn"
          >
            {directBar.label}
          </button>
          <button
            type="button"
            onClick={() => postToBook('BF_DIRECT_BAR_SHOW_DETAILS')}
            aria-label="Review details, discount codes & gift cards"
            className="w-12 rounded-full border border-gray-200 bg-white text-gray-500 flex items-center justify-center flex-shrink-0"
            data-testid="kiosk-direct-bar-details-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
          </button>
        </div>
      )}

      {/* 🖊️ POS waiver — full-screen blurred overlay owned by the shell so it
          covers the header too and sits perfectly centered on the real screen */}
      {directMode && posWaiver && (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closePosWaiver(); }}
          data-testid="kiosk-pos-waiver-overlay"
        >
          <div
            className="relative w-full max-w-[560px] bg-white rounded-3xl overflow-hidden flex flex-col shadow-[0_30px_80px_rgba(15,23,42,0.35)]"
            style={{ height: 'min(88vh, 820px)' }}
            data-testid="kiosk-pos-waiver-modal"
          >
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 flex-shrink-0 bg-white">
              <p className="text-[15px] font-extrabold text-gray-900">
                {isUAE ? 'Waiver · التنازل' : 'Waiver'}
                {posWaiver.signed > 0 && (
                  <span className="ml-2 text-xs font-bold text-emerald-600" data-testid="kiosk-pos-waiver-count">
                    {posWaiver.signed} {isUAE ? 'وقّع' : 'signed'} ✓
                  </span>
                )}
              </p>
              <button
                onClick={closePosWaiver}
                aria-label="Close"
                className="h-9 w-9 rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 flex items-center justify-center flex-shrink-0"
                data-testid="kiosk-pos-waiver-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <iframe
              src={`/sign/${orgSlug}?kiosk=true&pos=1&verify=${posWaiver.verify}`}
              title="Sign the waiver"
              className="flex-1 w-full border-0 bg-white"
              data-testid="kiosk-pos-waiver-iframe"
            />
          </div>
        </div>
      )}

      {/* 🖥️ POS boot splash — brand-blue takeover with the sonar logo animation
          (identical language to the native/Helm entry) until the register is ready */}
      {directMode && posBoot !== 'gone' && (
        <div
          data-testid="pos-boot-splash"
          style={{
            position: 'fixed', inset: 0, zIndex: 2147483000,
            background: '#2763EB',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            opacity: posBoot === 'exit' ? 0 : 1,
            transform: posBoot === 'exit' ? 'scale(1.035)' : 'scale(1)',
            transition: 'opacity 650ms cubic-bezier(0.22,1,0.36,1), transform 650ms cubic-bezier(0.22,1,0.36,1)',
            pointerEvents: posBoot === 'exit' ? 'none' : 'auto',
          }}
        >
          <style>{`
            @keyframes bfPosBreath { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
            @keyframes bfPosRipple { 0% { transform: scale(0.55); opacity: 0.35; } 80% { opacity: 0.06; } 100% { transform: scale(1.9); opacity: 0; } }
            @keyframes bfPosWordmark { 0% { opacity: 0; transform: translateY(10px); letter-spacing: 0.35em; } 100% { opacity: 0.92; transform: translateY(0); letter-spacing: 0.14em; } }
          `}</style>
          <div style={{ position: 'relative', width: 116, height: 116 }}>
            {[0, 1].map((i) => (
              <div key={i} style={{
                position: 'absolute', inset: -30, borderRadius: '50%',
                border: '1.5px solid rgba(255,255,255,0.5)',
                animation: `bfPosRipple 2.4s cubic-bezier(0.25,0.6,0.35,1) ${i * 1.2}s infinite`,
                opacity: 0,
              }} />
            ))}
            <img src="/logo-blue-512.png" alt="" draggable={false}
              style={{ width: '100%', height: '100%', animation: 'bfPosBreath 2.3s ease-in-out infinite', userSelect: 'none' }} />
          </div>
          <div style={{
            marginTop: 26, textAlign: 'center',
            animation: 'bfPosWordmark 900ms cubic-bezier(0.22,1,0.36,1) 250ms both',
          }}>
            <div style={{
              color: '#fff', fontSize: 15, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.14em',
            }}>
              Point of Sale
            </div>
            {/* Reserved line — the org name fades in WITHOUT shifting the layout */}
            <div style={{
              marginTop: 8, minHeight: 18, color: 'rgba(255,255,255,0.65)',
              fontSize: 12.5, fontWeight: 600, letterSpacing: '0.02em',
              opacity: org?.name ? 1 : 0, transition: 'opacity 400ms ease',
            }}>
              {org?.name || '\u00A0'}
            </div>
          </div>
        </div>
      )}

      {/* Booking peek sheet — everything a front-desk worker needs in one tap */}
      {sheetCode && (
        <KioskBookingSheet code={sheetCode} brand={brand} onClose={() => setSheetCode(null)} />
      )}
    </div>
  );
}
